import { Button, Form, Input, Space, Typography } from "antd"
import { useEffect, useState } from "react"

function getExistingAnswer(question, form) {
  const candidates = [
    form?.getFieldValue(question.uid),
    question?.response,
    question?.submission?.data,
    question?.answers?.find((a) => a?.isSelected)?.content?.title,
    question?.answers?.find((a) => a?.isSelected)?.value,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim()
    }
  }

  return null
}

export function FreeText({
  question,
  trackQuestionSubmission,
  form,
  disabled = false,
  required = false,
  inReview = false,
}) {
  const formValue = Form.useWatch(question.uid, form)
  const currentText = typeof formValue === "string" ? formValue : ""

  const [savedText, setSavedText] = useState(() => getExistingAnswer(question, form))
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (savedText !== null) return
    const existing = getExistingAnswer(question, form)
    if (existing) setSavedText(existing)
  }, [question, form, savedText])

  const hasSaved = savedText !== null
  const trimmedCurrent = currentText.trim()
  const isDirty = hasSaved ? trimmedCurrent !== savedText : trimmedCurrent.length > 0
  const canSave = !inReview && !disabled && isDirty && trimmedCurrent.length > 0

  const handleSave = async () => {
    if (!trimmedCurrent) {
      form?.validateFields([question.uid]).catch(() => {})
      return
    }

    setIsSaving(true)
    try {
      await trackQuestionSubmission({
        data: trimmedCurrent,
        typeUid: question.uid,
        type: question.type,
      })
      form?.setFieldsValue({ [question.uid]: trimmedCurrent })
      setSavedText(trimmedCurrent)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Form.Item
        name={question.uid}
        rules={[
          {
            required,
            whitespace: true,
            message: "Please enter an answer",
          },
        ]}
        style={{ marginBottom: 0 }}
      >
        <Input.TextArea
          readOnly={inReview}
          disabled={disabled && !inReview}
          autoSize={{ minRows: 4, maxRows: 12 }}
          placeholder={inReview ? undefined : "Type your answer here..."}
        />
      </Form.Item>

      {!inReview && !disabled && (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {hasSaved && !isDirty && (
            <Typography.Text type="success">Answer saved</Typography.Text>
          )}

          {isDirty && trimmedCurrent.length > 0 && (
            <Typography.Text type="warning">
              {hasSaved ? "Save your changes to keep them" : "Save your answer when you are done"}
            </Typography.Text>
          )}

          <Button type="primary" onClick={handleSave} loading={isSaving} disabled={!canSave}>
            Save answer
          </Button>
        </Space>
      )}
    </Space>
  )
}
